import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { Settings, LogOut, Link as LinkIcon, Users, CheckCircle2, AlertCircle, Info, User, Edit2, Save, X, Key, Unlink } from 'lucide-react';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { useAppNavigation } from '../../../app/hooks/useAppNavigation';
import { type LearnerAccount, type ParentAccount, type AccountsState, getLinkedParents, verifyPassword, hashPassword, isValidPassword } from '../../../accounts';
import { AppState } from '../../../app/types';
import { useLinkChild } from '../hooks/useLinkChild';
interface ParentPageProps {
  accounts: AccountsState;
  activeAccount: ParentAccount;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  viewingLearnerId: string | undefined;
  setViewingLearner: (id: string) => void;
  linkParentToLearnerById: (parentId: string, learnerId: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
}
export function SettingsPage({
  accounts,
  activeAccount,
  linkedLearners,
  linkParentToLearnerById
}: ParentPageProps) {
  const {
    clearActiveAccount,
    updateAccountNameById,
    updateAccountEmail,
    updatePassword,
    unlinkParentFromLearner
  } = useAppStore();
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized: true,
    switchAccount: () => {},
    setViewingLearner: () => {},
    loadDemoAccounts: () => {},
    clearActiveAccount
  });
  const {
    linkCode,
    setLinkCode,
    error,
    success,
    pendingLearner,
    pendingLearnerParents,
    handleSubmit: handleLinkChild,
    handleConfirmLink,
    handleCancelLink
  } = useLinkChild({
    accounts,
    parentId: activeAccount.id,
    linkedLearners,
    linkParentToLearnerById
  });
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(activeAccount.displayName);
  const [editEmail, setEditEmail] = useState(activeAccount.email || '');
  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  // Unlink state
  const [unlinkLearnerId, setUnlinkLearnerId] = useState<string | null>(null);
  const handleSaveProfile = () => {
    if (editName.trim()) {
      updateAccountNameById(activeAccount.id, editName.trim());
    }
    if (editEmail.trim() !== (activeAccount.email || '')) {
      updateAccountEmail(activeAccount.id, editEmail.trim());
    }
    setIsEditingProfile(false);
  };
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Vul alle velden in.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('De nieuwe wachtwoorden komen niet overeen.');
      return;
    }
    if (!isValidPassword(newPassword, 'parent')) {
      setPasswordError('Het nieuwe wachtwoord moet minimaal 6 tekens lang zijn.');
      return;
    }
    if (!activeAccount.passwordHash) {
      setPasswordError('Dit account heeft geen wachtwoord ingesteld.');
      return;
    }
    const isCorrect = await verifyPassword(currentPassword, activeAccount.passwordHash);
    if (!isCorrect) {
      setPasswordError('Het huidige wachtwoord is onjuist.');
      return;
    }
    const newHash = await hashPassword(newPassword);
    updatePassword(activeAccount.id, newHash);
    setPasswordSuccess('Je wachtwoord is succesvol gewijzigd.');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => {
      setIsChangingPassword(false);
      setPasswordSuccess('');
    }, 3000);
  };
  const handleConfirmUnlink = () => {
    if (unlinkLearnerId) {
      unlinkParentFromLearner(activeAccount.id, unlinkLearnerId);
      setUnlinkLearnerId(null);
    }
  };
  return <PageLayout title="Instellingen" description="Beheer je ouderaccount en gekoppelde kinderen" data-id="element-5633">
      <div className="max-w-3xl space-y-6" data-id="element-5634">
        {/* Account Info */}
        <SettingsSection title="Account Instellingen" description="Jouw persoonlijke gegevens" icon={Settings} accentColor="bg-emerald-100 text-emerald-600" action={!isEditingProfile ? <Button onClick={() => {
        setEditName(activeAccount.displayName);
        setEditEmail(activeAccount.email || '');
        setIsEditingProfile(true);
      }} variant="ghost" size="sm" data-id="element-5636">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-5637" /> Bewerk
              </Button> : null} data-id="element-5635">
          <div className="space-y-6" data-id="element-5638">
            {isEditingProfile ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-5639">
                <div data-id="element-5640">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5641">
                    Naam
                  </label>
                  <Input variant="admin" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Jouw naam" autoFocus data-id="element-5642" />
                </div>
                <div data-id="element-5643">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5644">
                    E-mailadres
                  </label>
                  <Input variant="admin" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="jouw@email.nl" data-id="element-5645" />
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 mt-2" data-id="element-5646">
                  <Button onClick={() => setIsEditingProfile(false)} variant="secondary" data-id="element-5647">
                    Annuleren
                  </Button>
                  <Button onClick={handleSaveProfile} variant="primary" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-5648">
                    Opslaan
                  </Button>
                </div>
              </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-5649">
                <div data-id="element-5650">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5651">
                    Naam
                  </label>
                  <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-5652">
                    {activeAccount.displayName}
                  </div>
                </div>
                <div data-id="element-5653">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5654">
                    E-mailadres
                  </label>
                  <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-5655">
                    {activeAccount.email || <span className="text-slate-400 italic" data-id="element-5656">
                        Niet ingesteld
                      </span>}
                  </div>
                </div>
              </div>}
          </div>
        </SettingsSection>

        {/* Password Change */}
        <SettingsSection title="Wachtwoord" description="Wijzig je wachtwoord" icon={Key} accentColor="bg-emerald-100 text-emerald-600" action={!isChangingPassword ? <Button onClick={() => setIsChangingPassword(true)} variant="ghost" size="sm" data-id="element-5658">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-5659" /> Wijzig
              </Button> : null} data-id="element-5657">
          {isChangingPassword ? <form onSubmit={handleChangePassword} className="space-y-4" data-id="element-5660">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5661">
                <div className="sm:col-span-2" data-id="element-5662">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5663">
                    Huidig wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-5664" />
                </div>
                <div data-id="element-5665">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5666">
                    Nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-5667" />
                </div>
                <div data-id="element-5668">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5669">
                    Bevestig nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-5670" />
                </div>
              </div>

              {passwordError && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3 rounded-lg border border-rose-100" data-id="element-5671">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5672" />
                  <p data-id="element-5673">{passwordError}</p>
                </div>}

              {passwordSuccess && <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100" data-id="element-5674">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5675" />
                  <p data-id="element-5676">{passwordSuccess}</p>
                </div>}

              <div className="flex justify-end gap-2 pt-2" data-id="element-5677">
                <Button type="button" onClick={() => {
              setIsChangingPassword(false);
              setPasswordError('');
              setPasswordSuccess('');
              setCurrentPassword('');
              setNewPassword('');
              setConfirmPassword('');
            }} variant="secondary" data-id="element-5678">
                  Annuleren
                </Button>
                <Button type="submit" variant="primary" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-5679">
                  Wachtwoord opslaan
                </Button>
              </div>
            </form> : <div className="text-sm text-slate-500" data-id="element-5680">
              Je wachtwoord is veilig opgeslagen. Klik op 'Wijzig' om een nieuw
              wachtwoord in te stellen.
            </div>}
        </SettingsSection>

        {/* Linked Children */}
        <SettingsSection title="Gekoppelde Kinderen" description="Beheer de kinderen die aan jouw account zijn gekoppeld" icon={Users} accentColor="bg-teal-100 text-teal-600" data-id="element-5681">
          {linkedLearners.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-id="element-5682">
              {linkedLearners.map(learner => {
            const otherParents = getLinkedParents(accounts, learner.id).filter(p => p.id !== activeAccount.id);
            return <div key={learner.id} className="p-4 rounded-xl border border-slate-200 bg-white shadow-sm hover:border-teal-200 transition-colors" data-id="element-5683">
                    <div className="flex items-center justify-between" data-id="element-5684">
                      <div className="flex items-center gap-3" data-id="element-5685">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-lg" data-id="element-5686">
                          {learner.displayName.charAt(0).toUpperCase()}
                        </div>
                        <div data-id="element-5687">
                          <div className="font-bold text-slate-900" data-id="element-5688">
                            {learner.displayName}
                          </div>
                          <div className="text-xs font-medium text-slate-500" data-id="element-5689">
                            {learner.username}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-5690">
                        <div className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100" data-id="element-5691">
                          Gekoppeld
                        </div>
                        <button onClick={() => setUnlinkLearnerId(learner.id)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors" title="Ontkoppelen" data-id="element-5692">
                          <Unlink className="w-4 h-4" data-id="element-5693" />
                        </button>
                      </div>
                    </div>
                    {otherParents.length > 0 && <div className="mt-3 pt-3 border-t border-slate-100" data-id="element-5694">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-5695">
                          Ook gekoppeld
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-id="element-5696">
                          {otherParents.map(p => <span key={p.id} className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-5697">
                              <User className="w-3 h-3" data-id="element-5698" />
                              {p.displayName}
                            </span>)}
                        </div>
                      </div>}
                  </div>;
          })}
            </div> : <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 border-dashed mb-8" data-id="element-5699">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mx-auto mb-3" data-id="element-5700">
                <Users className="w-6 h-6" data-id="element-5701" />
              </div>
              <p className="text-slate-500 font-medium" data-id="element-5702">
                Er zijn nog geen kinderen gekoppeld aan dit account.
              </p>
            </div>}

          <div className="pt-6 border-t border-slate-100" data-id="element-5703">
            <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-5704">
              <LinkIcon className="w-4 h-4 text-teal-500" data-id="element-5705" />
              Nieuw kind koppelen
            </h4>
            <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-5706">
              Voer de 6-cijferige koppelcode in die je van de docent hebt
              gekregen of die het kind in zijn/haar profiel kan vinden.
            </p>

            <form onSubmit={handleLinkChild} className="flex flex-col sm:flex-row gap-3" data-id="element-5707">
              <Input variant="admin" value={linkCode} onChange={e => setLinkCode(e.target.value.toUpperCase())} placeholder="Bijv. ABCDEF" className="uppercase font-mono tracking-wider" maxLength={6} data-id="element-5708" />
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white w-full sm:w-auto" data-id="element-5709">
                Koppelen
              </Button>
            </form>

            {error && <div className="mt-4 flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3.5 rounded-lg border border-rose-100" data-id="element-5710">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-rose-500" data-id="element-5711" />
                <p className="font-medium" data-id="element-5712">{error}</p>
              </div>}

            {success && <div className="mt-4 flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3.5 rounded-lg border border-emerald-100" data-id="element-5713">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-500" data-id="element-5714" />
                <p className="font-medium" data-id="element-5715">{success}</p>
              </div>}

            {/* Child preview before confirming link */}
            {pendingLearner && <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-xl" data-id="element-5716">
                <div className="flex items-center gap-3 mb-3" data-id="element-5717">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center" data-id="element-5718">
                    <User className="w-5 h-5 text-teal-600" data-id="element-5719" />
                  </div>
                  <div data-id="element-5720">
                    <p className="font-bold text-slate-900" data-id="element-5721">
                      {pendingLearner.displayName}
                    </p>
                    <p className="text-sm text-slate-500" data-id="element-5722">
                      Groep {pendingLearner.grade}
                    </p>
                  </div>
                </div>

                {pendingLearnerParents.length > 0 && <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 mb-3" data-id="element-5723">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5724" />
                    <span data-id="element-5725">
                      Dit kind is al gekoppeld aan:{' '}
                      {pendingLearnerParents.map(p => p.displayName).join(', ')}
                      . Jij wordt als extra ouder toegevoegd.
                    </span>
                  </div>}

                <div className="flex gap-2 justify-end" data-id="element-5726">
                  <Button variant="secondary" onClick={handleCancelLink} className="text-sm" data-id="element-5727">
                    Annuleren
                  </Button>
                  <Button onClick={handleConfirmLink} className="bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold" data-id="element-5728">
                    <CheckCircle2 className="w-4 h-4 mr-1" data-id="element-5729" />
                    Bevestig koppeling
                  </Button>
                </div>
              </motion.div>}
          </div>
        </SettingsSection>

        {/* Logout */}
        <SettingsSection title="Uitloggen" description="Log uit van je ouderaccount" icon={LogOut} accentColor="bg-rose-100 text-rose-600" className="bg-rose-50/30 border-rose-100" data-id="element-5730">
          <div className="flex justify-end" data-id="element-5731">
            <Button variant="secondary" className="w-full sm:w-auto text-rose-600 border-rose-200 hover:bg-rose-50 hover:border-rose-300" onClick={nav.handleLogout} data-id="element-5732">
              <LogOut className="w-4 h-4 mr-2" data-id="element-5733" />
              Uitloggen
            </Button>
          </div>
        </SettingsSection>
      </div>

      <ConfirmDialog isOpen={!!unlinkLearnerId} onClose={() => setUnlinkLearnerId(null)} onConfirm={handleConfirmUnlink} title="Kind ontkoppelen" message={`Weet je zeker dat je jezelf wilt ontkoppelen van ${linkedLearners.find(l => l.id === unlinkLearnerId)?.displayName}? Je kunt daarna geen voortgang meer inzien.`} confirmText="Ja, ontkoppelen" cancelText="Annuleren" variant="danger" data-id="element-5734" />
    </PageLayout>;
}